import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {AbortController | undefined} */
  #hoverPreviewController;

  /**
   * Aisle: the gallery image a hovered colour swatch is previewing, with what to restore.
   * @type {{ fieldset: HTMLFieldSetElement, image: HTMLImageElement, src: string, srcset: string, alt: string, label: HTMLElement | null, labelText: string } | undefined}
   */
  #hoverPreview;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // Aisle: preview a colour's main image while its swatch is hovered (mouse devices only)
    this.#hoverPreviewController?.abort();
    if (this.hasAttribute('data-swatch-hover-preview') && window.matchMedia('(hover: hover)').matches) {
      this.#hoverPreviewController = new AbortController();
      const { signal } = this.#hoverPreviewController;
      this.addEventListener('pointerover', this.#previewSwatch, { signal });
      this.addEventListener('pointerout', this.#endSwatchPreview, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#hoverPreviewController?.abort();
    this.#hoverPreview = undefined;
  }

  /**
   * Aisle: shows the hovered colour's main image in the gallery and its name in the legend.
   * @param {PointerEvent} event
   */
  #previewSwatch = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const input = event.target.closest('label')?.querySelector('input[data-preview-srcset]');
    const fieldset = input?.closest('fieldset');
    if (!(input instanceof HTMLInputElement) || !fieldset) return;

    if (!this.#hoverPreview?.image.isConnected) {
      const image = this.#currentGalleryImage();
      if (!image) return;

      const label = fieldset.querySelector('.variant-option__swatch-value');
      this.#hoverPreview = {
        fieldset,
        image,
        src: image.getAttribute('src') ?? '',
        srcset: image.getAttribute('srcset') ?? '',
        alt: image.alt,
        label: label instanceof HTMLElement ? label : null,
        labelText: label?.textContent ?? '',
      };
      this.#preloadSwatchImages(fieldset, image.sizes);
    }

    const { image, label } = this.#hoverPreview;
    image.srcset = input.dataset.previewSrcset ?? '';
    image.src = input.dataset.previewSrc ?? '';
    image.alt = input.dataset.previewAlt ?? '';
    if (label) label.textContent = input.value;
  };

  /**
   * Aisle: restores the selected colour once the pointer leaves the swatches.
   * @param {PointerEvent} event
   */
  #endSwatchPreview = (event) => {
    const preview = this.#hoverPreview;
    if (!preview) return;
    if (event.relatedTarget instanceof Node && preview.fieldset.contains(event.relatedTarget)) return;

    this.#hoverPreview = undefined;
    preview.image.srcset = preview.srcset;
    preview.image.src = preview.src;
    preview.image.alt = preview.alt;
    if (preview.label) preview.label.textContent = preview.labelText;
  };

  /**
   * Aisle: the gallery image currently on screen.
   * @returns {HTMLImageElement | null}
   */
  #currentGalleryImage() {
    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
    const slideshow = /** @type {import('./slideshow').Slideshow | null | undefined} */ (
      gallery?.querySelector('slideshow-component')
    );
    const slide = slideshow?.slides?.[slideshow.current] ?? gallery?.querySelector('slideshow-slide');
    const image = slide?.querySelector('img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * Aisle: warms the cache with every colour's preview at the gallery's rendered size.
   * @param {HTMLFieldSetElement} fieldset
   * @param {string} sizes
   */
  #preloadSwatchImages(fieldset, sizes) {
    for (const input of fieldset.querySelectorAll('input[data-preview-srcset]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const image = new Image();
      image.sizes = sizes;
      image.srcset = input.dataset.previewSrcset ?? '';
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Aisle: a clicked swatch keeps its preview until the new gallery arrives
    this.#hoverPreview = undefined;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Variant changes within the same product only update this section (every variant:update listener
    // lives inside it), so render just this section instead of the whole page.
    let requestUrl = this.buildRequestUrl(selectedOption);
    const sectionId = isOnProductPage && !morphElementSelector ? this.closest('.shopify-section')?.id.replace('shopify-section-', '') : '';
    if (sectionId) {
      const sectionUrl = new URL(requestUrl, window.location.origin);
      sectionUrl.searchParams.set('section_id', sectionId);
      requestUrl = sectionUrl.toString();
    }

    this.fetchUpdatedSection(requestUrl, morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = new URL(newUrl, window.location.origin).pathname;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.delete('variant');
    for (const [key, value] of new URLSearchParams(params.join('&'))) {
      requestUrl.searchParams.set(key, value);
    }
    return requestUrl.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const requestController = this.#abortController;

    fetch(requestUrl, { signal: requestController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        if (requestController.signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        const nextPicker = html.querySelector('variant-picker[data-pdp-controls="true"]');
        const colors = nextPicker?.querySelector('.variant-option--swatches');
        // A linked pack can have different color values. Resolve a real destination
        // variant before showing the page instead of displaying an invalid combination.
        if (morphElementSelector === 'main' && colors && !colors.querySelector('input:checked')) {
          /** @type {{ id: number, options: string[] }[]} */
          const candidates = JSON.parse(nextPicker?.querySelector('[data-pdp-available-variants]')?.textContent || '[]');
          const selectedSize = this.querySelector('[data-option-kind="size"] input:checked');
          const size = selectedSize instanceof HTMLInputElement ? selectedSize.value : '';
          const colorOrder = Array.from(colors.querySelectorAll('input')).map((input) => input.value);
          const matchingSize = candidates.filter((candidate) => size && candidate.options.includes(size));
          const eligible = matchingSize.length ? matchingSize : candidates;
          const fallback = colorOrder.map((color) => eligible.find((candidate) => candidate.options.includes(color))).find(Boolean);
          if (fallback) {
            const fallbackUrl = new URL(requestUrl, window.location.origin);
            fallbackUrl.searchParams.delete('option_values');
            fallbackUrl.searchParams.set('variant', String(fallback.id));
            const response = await fetch(fallbackUrl, { signal: requestController.signal });
            if (!response.ok) throw new Error(`Product fallback failed: ${response.status}`);
            html = new DOMParser().parseFromString(await response.text(), 'text/html');
            if (requestController.signal.aborted) return;
            history.replaceState({}, '', fallbackUrl.toString());
          }
        }
        // A linked product always arrives with a variant preselected. Keep the shopper's size only
        // when they had chosen one and the new product selected that same size; otherwise ask again.
        if (morphElementSelector === 'main') {
          const nextDetails = html.querySelector('pdp-purchase-component');
          const currentDetails = this.closest('pdp-purchase-component');
          const chosenSize = currentDetails?.hasAttribute('data-needs-size')
            ? ''
            : this.querySelector('[data-option-kind="size"] input:checked')?.getAttribute('value') ?? '';
          const nextSize =
            html.querySelector('variant-picker[data-pdp-controls="true"] [data-option-kind="size"] input:checked')?.getAttribute('value') ?? '';
          if (!chosenSize || chosenSize !== nextSize) nextDetails?.setAttribute('data-has-selected-variant', 'false');
        }
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[data-selected-variant], variant-picker script[type="application/json"]:not([data-pdp-available-variants])`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          const details = document.querySelector('main pdp-purchase-component');
          if (details && 'refreshSelection' in details && typeof details.refreshSelection === 'function') {
            details.refreshSelection();
          }
          restartJudgeMe();
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

/**
 * Judge.me's loader reveals and sets up its widgets once per page load, so a linked-product swap
 * leaves the new product's reviews hidden. Run the loader again so it starts over on the new markup.
 * Relies on Judge.me internals (`_loaderExecuted`, `loadScript.requestedUrls`); skips when they change.
 */
function restartJudgeMe() {
  const jdgm = /** @type {any} */ (window).jdgm;
  const requestedUrls = jdgm?.loadScript?.requestedUrls;
  const loader = Array.from(document.scripts).find((script) => /\/judgeme[^/]*\/assets\/loader\.js/.test(script.src));
  if (!jdgm?._loaderExecuted || !Array.isArray(requestedUrls) || !loader) return;

  jdgm._loaderExecuted = false;
  // Lets the loader execute its widget scripts again instead of skipping them as already loaded.
  requestedUrls.length = 0;

  document.querySelector('script[data-judge-me-restart]')?.remove();
  const script = document.createElement('script');
  script.src = loader.src;
  script.dataset.judgeMeRestart = '';
  document.head.append(script);
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
